import React from 'react';

import {SchemaTag} from 'folder-items/FileConstants';
import InternalLink from 'notebook/mediocre/mentions/InternalLink';
import withSelectorLoading from 'registry/schema-field-selector/withSelectorLoading';
import {SchemaFieldSelector, SchemaFieldSelectorOption} from 'schemas/SchemaFieldSelectorConstants';
import ControlledSearchInput from 'schemas/tags/ControlledSearchInput';
import {areAllOptionsValid, getSelectorOptions} from 'schemas/tags/SchemaFieldSelectorUtil';
import AttachDropdown from 'ui-library/attach-dropdown/AttachDropdown';
import {AttachDropdownItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementKeyboardEvent} from 'util/Types';

interface SelectorMenuOptionProps {
  name: string;
  isSelected: boolean;
}

function SelectorMenuOption(props: SelectorMenuOptionProps): JSX.Element {
  return (
    <Tooltip
      title={props.name}
      disabled={props.name.length < 35 /* best-effort conservative estimate */}
      delay={{show: 150}}
    >
      <div className="u-height-full u-vertical-center-items u-lr-split legacy-font-proxima-14 u-pointer">
        <span className="u-small-padding u-ellipsis-text">{props.name}</span>
        {props.isSelected && (
          <span className="u-success-color u-small-padding">
            <BenchlingIcon icon="check" />
          </span>
        )}
      </div>
    </Tooltip>
  );
}

interface SelectorInputProps {
  autoFocus: boolean;
  inputValue: string;
  onChange: ({
    schemaFieldSelectorOptionIds,
    value,
  }: {
    schemaFieldSelectorOptionIds: Array<string>;
    value: string;
  }) => void;
  onBlur?: () => void;
  onUpdateInputValue: (inputValue: string) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  tagValue: SchemaTag;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;

  // Connected
  schemaFieldSelector: SchemaFieldSelector;
}

/**
 * Selector input designed for table cell inputs.
 */
export class SelectorInput extends React.Component<SelectorInputProps> {
  _getOptionIds(): Array<string> {
    if (this.props.tagValue.schemaFieldSelectorOptionIds == null) {
      return [];
    }
    if (
      !areAllOptionsValid(this.props.tagValue.schemaFieldSelectorOptionIds, this.props.schemaFieldSelector)
    ) {
      // Some of the option ids are not found in the selector. This may be caused by pasting a value from
      // a different column.
      return [];
    }
    return this.props.tagValue.schemaFieldSelectorOptionIds
      ? this.props.tagValue.schemaFieldSelectorOptionIds
      : [];
  }

  _hasTypedValue(): boolean {
    return this._getOptionIds().length > 0;
  }

  _canSelectAdditional(): boolean {
    const {tagSchemaField} = this.props.tagValue;
    return !this._hasTypedValue() || tagSchemaField.isMulti;
  }

  _handleRemoveLastValue = (): void => {
    const optionIds = this._getOptionIds();
    if (optionIds.length > 0) {
      const schemaFieldSelectorOptionIds = optionIds.slice(0, optionIds.length - 1);
      this.props.onChange({
        schemaFieldSelectorOptionIds,
        value: this._getTextValue(schemaFieldSelectorOptionIds),
      });
    }
  };

  _handleUpdateInputValue = (inputValue: string): void => {
    const optionIds = this._getOptionIds();

    this.props.onUpdateInputValue(inputValue);
    this.props.onChange({
      schemaFieldSelectorOptionIds: optionIds,
      value: this._getTextValue(optionIds),
    });
  };

  _getTextValue(optionIds: Array<string>): string {
    const options = getSelectorOptions(optionIds, this.props.schemaFieldSelector);
    return options.map((option: SchemaFieldSelectorOption) => option.name).join(', ');
  }

  _handleSelectAttachment = (id: string): void => {
    const optionIds = this._getOptionIds();
    const schemaFieldSelectorOptionIds = optionIds.includes(id)
      ? optionIds.filter((optionId) => optionId !== id)
      : this._canSelectAdditional()
        ? [...optionIds, id]
        : [id];
    this.props.onUpdateInputValue('');
    this.props.onChange({
      schemaFieldSelectorOptionIds,
      value: this._getTextValue(schemaFieldSelectorOptionIds),
    });
  };

  _getResultItems(): Array<AttachDropdownItem> {
    const {schemaFieldSelector} = this.props;
    const optionIds = this._getOptionIds();
    if (schemaFieldSelector == null) {
      return [];
    }
    return schemaFieldSelector.schemaFieldSelectorOptions
      .filter(({name}) => name.toLowerCase().includes(this.props.inputValue.toLowerCase()))
      .map(({id, name}) => ({
        name,
        item_id: id,
        item: {},
        noIcon: true,
        isSelected: optionIds.includes(id),
      }));
  }

  _renderSearchDropdown = (ref: React.RefObject<AttachDropdown>): JSX.Element => (
    <AttachDropdown
      ref={ref}
      query={this.props.inputValue}
      selectAttachment={this._handleSelectAttachment}
      results={this._getResultItems()}
      width="100%"
      errorMessage={null}
      height={AttachDropdown.HEIGHTS.MEDIUM}
      // If any query is typed, the user is actively picking an option, so enter should select it.
      // Otherwise, enter should accept the current selection.
      defaultOptionSelected={this.props.inputValue !== ''}
      searching={false}
      getRenderOptionInfo={(result: AttachDropdownItem) => ({
        optionContent: <SelectorMenuOption name={result.name} isSelected={result.isSelected!} />,
        hoverable: true,
        clickable: true,
      })}
    />
  );

  /** Unselect a specific option */
  _removeSelectedOption = (optionId: string): void => {
    const schemaFieldSelectorOptionIds = this._getOptionIds().filter((opId) => optionId !== opId);
    this.props.onChange({
      schemaFieldSelectorOptionIds,
      value: this._getTextValue(schemaFieldSelectorOptionIds),
    });
  };

  _renderSelectedValues = (): JSX.Element => {
    const optionIds = this._getOptionIds();
    const selectedOptions = getSelectorOptions(optionIds, this.props.schemaFieldSelector);
    const Wrapper = this.props.isOutsideTable ? 'span' : 'div';
    return (
      <Wrapper>
        {selectedOptions.map(({id, name}: SchemaFieldSelectorOption, i: number) => (
          <span key={i}>
            <InternalLink
              className="chip-container chip--tableCell"
              internalLink={{name}}
              onRemove={() => {
                this._removeSelectedOption(id);
              }}
            />
          </span>
        ))}
      </Wrapper>
    );
  };

  render(): JSX.Element | null {
    if (!this.props.schemaFieldSelector) {
      return null;
    }
    return (
      <ControlledSearchInput
        shouldForwardKeydown={true}
        canSelectAdditional={this._canSelectAdditional()}
        hasTypedValue={this._hasTypedValue()}
        onRemoveLastValue={this._handleRemoveLastValue}
        autoFocus={this.props.autoFocus}
        inputValue={this.props.inputValue}
        onBlur={this.props.onBlur}
        onUpdateInputValue={this._handleUpdateInputValue}
        onKeyDown={this.props.onKeyDown}
        renderSelectedValues={this._renderSelectedValues}
        renderSearchDropdown={this._renderSearchDropdown}
        shouldCloseOnInsideClick={false}
        isOutsideTable={this.props.isOutsideTable}
        showCaret={this.props.isOutsideTable}
        placeholder=""
      />
    );
  }
}

export default withSelectorLoading(
  SelectorInput,
  (props: SelectorInputProps) => props.tagValue.tagSchemaField.schemaFieldSelectorId ?? null
);
